import { enhance } from '~plugins/utils'
import type { EnhancedPost } from '~base/common/types'

interface PostWithId {
  postId: number | string
}

interface NuxtApp {
  apolloProvider: {
    defaultClient: {
      query: (
        options: object
      ) => Promise<{ data: { posts: { edges: { node: any }[] } } }>
    }
  }
  $axios: {
    $get: <T>(url: string) => Promise<T>
  }
  $config: any
}

function postId(post: PostWithId): string {
  return String(post.postId)
}

export function orderPostsByIds<T extends PostWithId>(
  ids: number[],
  posts: T[]
): T[] {
  const postsById = new Map(posts.map((post) => [postId(post), post]))

  return ids
    .map(String)
    .map((id) => postsById.get(id))
    .filter((post): post is T => Boolean(post))
}

export function mergePersonalizedPosts<T extends PostWithId>(
  recommendedPosts: T[],
  currentPosts: T[]
): T[] {
  const seen = new Set<string>()

  return [...recommendedPosts, ...currentPosts].filter((post) => {
    const id = postId(post)
    if (seen.has(id)) {
      return false
    }
    seen.add(id)
    return true
  })
}

export async function fetchPersonalizedPosts(
  app: NuxtApp
): Promise<EnhancedPost[]> {
  try {
    const { articleIds } = await app.$axios.$get<{ articleIds: number[] }>(
      '/api/neue/personalization'
    )

    if (!articleIds.length) {
      console.log('No personalized article IDs found, returning empty array.')
      return []
    }

    const { data } = await app.apolloProvider.defaultClient.query({
      query: require('~base/graphql/queries/PostsByIds.gql'),
      variables: { ids: articleIds, first: articleIds.length },
      fetchPolicy: 'network-only',
    })

    const posts = data.posts.edges
      .map((edge: { node: EnhancedPost }) => edge.node)
      .map((post: EnhancedPost) => enhance(post, app.$config))
      .filter((post: EnhancedPost | undefined): post is EnhancedPost =>
        Boolean(post)
      )

    return orderPostsByIds(articleIds, posts).map((post) => ({
      ...post,
      isPersonalized: true,
    }))
  } catch (error) {
    // A personalization failure must not prevent the standard homepage feed.
    return []
  }
}
